// ═══════════════════════════════════════════════════════════════
// GeoOps — Camera Source Fetcher
// Fetches cameras from user-configured sources stored in the
// cameraSources table. No hardcoded 511 state integrations.
// ═══════════════════════════════════════════════════════════════

import { haversine, type LatLng } from './perimeterGeo';
import type { CameraSource } from '../store/geoopsDataStore';

// ── Types ────────────────────────────────────────────────────

export interface TrafficCamera {
  id: number;
  name: string;
  location: string;
  roadway: string;
  direction: string;
  lat: number;
  lng: number;
  distance: number;
  imageUrl: string;
  videoUrl: string;
  imageId: number;
  streamUrl?: string;
  sourceName?: string;
}

// ── Field mapping helper ─────────────────────────────────────

interface FieldMap {
  lat: string;
  lng: string;
  name?: string;
  id?: string;
  imageUrl?: string;
  streamUrl?: string;
  roadway?: string;
  direction?: string;
}

function parseFieldMap(json: string): FieldMap {
  try {
    return JSON.parse(json);
  } catch {
    return { lat: 'lat', lng: 'lng' };
  }
}

function getField(obj: Record<string, unknown>, key: string | undefined): string {
  if (!key) return '';
  return String(obj[key] ?? '');
}

function hashId(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

// ── ArcGIS MapServer / FeatureServer ─────────────────────────
// Appends /query?where=1=1&outFields=*&f=json to the base URL
// and reads features[].attributes

async function fetchArcGISCameras(
  source: CameraSource,
  center: LatLng,
  maxDist: number,
): Promise<TrafficCamera[]> {
  const baseUrl = source.listEndpoint.replace(/\/$/, '');
  const url = `${baseUrl}/query?where=1%3D1&outFields=*&f=json`;
  const headers: HeadersInit = {};
  if (source.apiKey) headers['X-Esri-Authorization'] = `Bearer ${source.apiKey}`;

  const resp = await fetch(url, { headers });
  if (!resp.ok) return [];
  const data = await resp.json() as { features?: Array<{ attributes: Record<string, unknown>; geometry?: { x: number; y: number } }> };
  if (!data.features) return [];

  const fm = parseFieldMap(source.fieldMapping);
  const cameras: TrafficCamera[] = [];

  for (const f of data.features) {
    const a = f.attributes;
    // Lat/lng may be in attributes or geometry (ArcGIS projected)
    const lat = Number(a[fm.lat] ?? f.geometry?.y ?? 0);
    const lng = Number(a[fm.lng] ?? f.geometry?.x ?? 0);
    if (!lat || !lng) continue;

    const dist = haversine(center.lat, center.lng, lat, lng);
    if (dist > maxDist) continue;

    const rawId = getField(a, fm.id) || String(lat + lng);
    const id = isNaN(Number(rawId)) ? hashId(rawId) : Number(rawId);

    const imageUrl = fm.imageUrl
      ? source.snapshotEndpoint
        ? source.snapshotEndpoint.replace('{id}', rawId)
        : getField(a, fm.imageUrl)
      : source.snapshotEndpoint
        ? source.snapshotEndpoint.replace('{id}', rawId)
        : '';

    cameras.push({
      id,
      name: getField(a, fm.name) || `Camera ${rawId}`,
      location: getField(a, fm.name) || '',
      roadway: getField(a, fm.roadway),
      direction: getField(a, fm.direction),
      lat,
      lng,
      distance: dist,
      imageUrl,
      videoUrl: '',
      imageId: id,
      streamUrl: fm.streamUrl ? getField(a, fm.streamUrl) || undefined : undefined,
      sourceName: source.name,
    });
  }
  return cameras;
}

// ── Generic JSON endpoint ────────────────────────────────────
// User provides a REST URL that returns a JSON array of objects.
// fieldMapping tells us which fields have lat/lng/name/imageUrl/id.

async function fetchGenericJSON(
  source: CameraSource,
  center: LatLng,
  maxDist: number,
): Promise<TrafficCamera[]> {
  const headers: HeadersInit = { Accept: 'application/json' };
  if (source.apiKey) headers['Authorization'] = `Bearer ${source.apiKey}`;

  const resp = await fetch(source.listEndpoint, { headers });
  if (!resp.ok) return [];

  let entries: unknown;
  try { entries = await resp.json(); } catch { return []; }

  // Unwrap common envelope shapes: { cameras: [...] }, { data: [...] }, { features: [...] }
  if (entries && typeof entries === 'object' && !Array.isArray(entries)) {
    const obj = entries as Record<string, unknown>;
    entries = obj.cameras ?? obj.data ?? obj.features ?? obj.items ?? obj.results ?? entries;
  }

  if (!Array.isArray(entries)) return [];

  const fm = parseFieldMap(source.fieldMapping);
  const cameras: TrafficCamera[] = [];

  for (const entry of entries) {
    const a = entry as Record<string, unknown>;
    const lat = Number(a[fm.lat] ?? 0);
    const lng = Number(a[fm.lng] ?? 0);
    if (!lat || !lng) continue;

    const dist = haversine(center.lat, center.lng, lat, lng);
    if (dist > maxDist) continue;

    const rawId = getField(a, fm.id) || String(lat + lng);
    const id = isNaN(Number(rawId)) ? hashId(rawId) : Number(rawId);

    const imageUrl = fm.imageUrl
      ? source.snapshotEndpoint
        ? source.snapshotEndpoint.replace('{id}', rawId)
        : getField(a, fm.imageUrl)
      : source.snapshotEndpoint
        ? source.snapshotEndpoint.replace('{id}', rawId)
        : '';

    cameras.push({
      id,
      name: getField(a, fm.name) || `Camera ${rawId}`,
      location: getField(a, fm.name) || '',
      roadway: getField(a, fm.roadway),
      direction: getField(a, fm.direction),
      lat,
      lng,
      distance: dist,
      imageUrl,
      videoUrl: '',
      imageId: id,
      streamUrl: fm.streamUrl ? getField(a, fm.streamUrl) || undefined : undefined,
      sourceName: source.name,
    });
  }
  return cameras;
}

// ── Single image URL source ──────────────────────────────────
// Provider type 'generic_image': the listEndpoint IS the image URL.
// regionLat/regionLng define where to place the camera on the map.

function makeSingleImageCamera(source: CameraSource, center: LatLng, maxDist: number): TrafficCamera[] {
  if (!source.regionLat || !source.regionLng) return [];
  const dist = haversine(center.lat, center.lng, source.regionLat, source.regionLng);
  if (dist > maxDist) return [];
  return [{
    id: hashId(source.listEndpoint),
    name: source.name,
    location: source.name,
    roadway: '',
    direction: '',
    lat: source.regionLat,
    lng: source.regionLng,
    distance: dist,
    imageUrl: source.listEndpoint,
    videoUrl: source.streamEndpoint || '',
    imageId: hashId(source.listEndpoint),
    streamUrl: source.streamEndpoint || undefined,
    sourceName: source.name,
  }];
}

// ── Public API ───────────────────────────────────────────────

/**
 * Fetch cameras from all active configured camera sources within radiusMiles.
 * Falls back gracefully if any source fails.
 */
export async function fetchCamerasNearby(
  center: LatLng,
  radiusMiles: number,
  sources: CameraSource[],
): Promise<TrafficCamera[]> {
  const maxDist = radiusMiles * 1.5;
  const activeSources = sources.filter(s => s.status === 'active');

  if (activeSources.length === 0) return [];

  const results = await Promise.all(
    activeSources.map(source => {
      try {
        switch (source.provider) {
          case 'arcgis':
            return fetchArcGISCameras(source, center, maxDist).catch(() => [] as TrafficCamera[]);
          case 'generic_image':
            return Promise.resolve(makeSingleImageCamera(source, center, maxDist));
          case 'generic_json':
          default:
            return fetchGenericJSON(source, center, maxDist).catch(() => [] as TrafficCamera[]);
        }
      } catch {
        return Promise.resolve([] as TrafficCamera[]);
      }
    })
  );

  const all = results.flat();
  all.sort((a, b) => a.distance - b.distance);
  return all;
}

/**
 * Fetch HLS/RTSP stream URL for a camera.
 * If the camera has a direct streamUrl, returns it.
 * If the source has a streamEndpoint template, substitutes {id}.
 */
export async function fetchCameraStream(cam: TrafficCamera): Promise<string | null> {
  if (cam.streamUrl) return cam.streamUrl;
  return null;
}

// ── GIS Feature Fetcher ──────────────────────────────────────

export interface GISFeature {
  name: string;
  category: string;
  lat: number;
  lng: number;
  properties: Record<string, unknown>;
  sourceName: string;
}

/**
 * Fetch features from a GIS source and normalize to GISFeature[].
 * Supports ArcGIS REST Feature Service, WFS, and static GeoJSON.
 */
export async function fetchGISFeatures(source: import('../store/geoopsDataStore').GISSource): Promise<GISFeature[]> {
  const features: GISFeature[] = [];
  let auth: Record<string, string> = {};
  try { auth = JSON.parse(source.auth || '{}'); } catch { /* ignore */ }

  const headers: HeadersInit = { Accept: 'application/json' };
  if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`;
  if (auth.type === 'basic' && auth.username) {
    headers['Authorization'] = `Basic ${btoa(`${auth.username}:${auth.password || ''}`)}`;
  }

  let catMap: Record<string, string> = {};
  try { catMap = JSON.parse(source.categoryMapping || '{}'); } catch { /* ignore */ }

  try {
    if (source.protocol === 'arcgis') {
      const url = `${source.url.replace(/\/$/, '')}/query?where=1%3D1&outFields=*&f=geojson`;
      const resp = await fetch(url, { headers });
      if (!resp.ok) return [];
      const gj = await resp.json() as GeoJSON.FeatureCollection;
      for (const f of gj.features || []) {
        if (f.geometry?.type !== 'Point') continue;
        const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates;
        const props = (f.properties || {}) as Record<string, unknown>;
        const name = String(props.Name ?? props.NAME ?? props.name ?? props.FACILITYNAME ?? props.LABEL ?? 'Feature');
        const rawCat = Object.values(props).find(v => catMap[String(v)]);
        const category = rawCat ? catMap[String(rawCat)] : (catMap['*'] || 'Infrastructure');
        features.push({ name, category, lat, lng, properties: props, sourceName: source.name });
      }
    } else if (source.protocol === 'wfs') {
      const typeName = source.layerName || 'feature';
      const url = `${source.url}?service=WFS&version=2.0.0&request=GetFeature&typeName=${encodeURIComponent(typeName)}&outputFormat=application/json&count=2000`;
      const resp = await fetch(url, { headers });
      if (!resp.ok) return [];
      const gj = await resp.json() as GeoJSON.FeatureCollection;
      for (const f of gj.features || []) {
        if (f.geometry?.type !== 'Point') continue;
        const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates;
        const props = (f.properties || {}) as Record<string, unknown>;
        const name = String(props.name ?? props.NAME ?? props.LABEL ?? 'Feature');
        features.push({ name, category: catMap['*'] || 'Infrastructure', lat, lng, properties: props, sourceName: source.name });
      }
    } else {
      // geojson — fetch directly
      const resp = await fetch(source.url, { headers });
      if (!resp.ok) return [];
      const gj = await resp.json() as GeoJSON.FeatureCollection;
      for (const f of gj.features || []) {
        if (f.geometry?.type !== 'Point') continue;
        const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates;
        const props = (f.properties || {}) as Record<string, unknown>;
        const name = String(props.name ?? props.NAME ?? props.LABEL ?? 'Feature');
        features.push({ name, category: catMap['*'] || 'Infrastructure', lat, lng, properties: props, sourceName: source.name });
      }
    }
  } catch (e) {
    console.error('[GeoOps] fetchGISFeatures', source.name, e);
  }

  return features;
}
